/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, DateField, Text } from '@/shared/ui';
import { Select } from '@/shared/ui/components/form/inputs/Select';

import { MicroLabel, PanelEmptyState, Pill, SurfaceCard, formatCalendarDate } from './sale-detail-atoms';
import { milestoneLabel } from '../constants/sale-states';
import type { Sale, SalePaperworkMilestoneType } from '../types/sale.types';

export interface SalePaperworkPanelProps {
  sale: Sale;
  /** The agency's configured milestone types — what may be added here. */
  milestoneTypes: SalePaperworkMilestoneType[];
  /** Persists one changed date. */
  onRecordPaperwork: (dates: Record<string, string>) => Promise<void>;
  /** Terminal sales are a record to read, not a form to fill. */
  readOnly: boolean;
}

/**
 * The paperwork as milestones the broker adds, not a fixed set forced on
 * every sale.
 *
 * Escritura, ITBI, the RI registration and the financing approval land out
 * of order and sometimes weeks apart, and not every sale has all four — a
 * cash purchase never gets a financing date, and some brokers only track
 * what actually applies. Only milestones already recorded are shown; the
 * rest are one "add" away rather than sitting pre-rendered as pending rows
 * nobody asked for.
 *
 * @param props.sale - The sale record holding the dates.
 * @param props.onRecordPaperwork - Persists a changed date.
 * @param props.readOnly - Whether the sale has reached a terminal state.
 * @returns The rendered paperwork panel.
 */
export function SalePaperworkPanel({
  sale,
  milestoneTypes,
  onRecordPaperwork,
  readOnly,
}: SalePaperworkPanelProps) {
  const { t } = useTranslation('features/sales');
  const [isAdding, setIsAdding] = useState(false);
  const [draftKey, setDraftKey] = useState<string>('');
  const [draftDate, setDraftDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Every configured milestone gets a row, recorded or not, so the panel
  // shows the whole lavratura rather than only the part already done.
  const added = useMemo(
    () => [...milestoneTypes].sort((a, b) => a.order - b.order),
    [milestoneTypes],
  );
  const available = useMemo(
    () => milestoneTypes.filter((m) => !sale.paperwork[m.id]),
    [sale, milestoneTypes],
  );

  async function save(milestoneId: string, value: string) {
    try {
      await onRecordPaperwork({ [milestoneId]: value });
      toast.success(t('detail.paperwork.saved'));
    } catch {
      toast.error(t('detail.paperwork.saveFailed'));
    }
  }

  async function confirmAdd() {
    const milestone = available.find((m) => m.id === draftKey);
    if (!milestone || !draftDate) return;
    setSaving(true);
    try {
      await save(milestone.id, draftDate);
      setIsAdding(false);
      setDraftKey('');
      setDraftDate('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SurfaceCard
      testId="sale-paperwork-panel"
      className="flex flex-col gap-(--space-3)"
    >
      <MicroLabel>{t('detail.paperwork.title')}</MicroLabel>

      {added.length === 0 && !isAdding && (
        <PanelEmptyState
          message={t('detail.paperwork.noneAdded')}
          hint={t('detail.paperwork.noneAddedHint')}
        />
      )}

      {added.length > 0 && (
        <div className="flex flex-col">
          {added.map((milestone) => (
            <MilestoneRow
              key={milestone.id}
              milestone={milestone}
              value={sale.paperwork[milestone.id] || ''}
              readOnly={readOnly}
              onSave={save}
            />
          ))}
        </div>
      )}

      {!readOnly && available.length > 0 && (
        isAdding ? (
          <div
            className="flex flex-wrap items-end gap-(--space-2) border-t border-(--border-subtle) pt-(--space-3)"
            data-testid="paperwork-add-form"
          >
            <div className="min-w-40 flex-1">
              <Select
                label={t('detail.paperwork.milestoneType')}
                placeholder={t('detail.paperwork.selectMilestone')}
                value={draftKey}
                onChange={(val) => setDraftKey(String(val ?? ''))}
                data={available.map((m) => ({
                  value: m.id,
                  label: milestoneLabel(m, t),
                }))}
              />
            </div>
            <DateField
              label={t('detail.paperwork.milestoneDate')}
              value={draftDate}
              onChange={(e) => setDraftDate(e.target.value)}
              className="w-40"
            />
            <Button
              size="sm"
              variant="solid"
              disabled={!draftKey || !draftDate || saving}
              loading={saving}
              data-testid="paperwork-add-confirm"
              onClick={() => void confirmAdd()}
            >
              {t('detail.paperwork.addConfirm')}
            </Button>
            <Button
              size="sm"
              variant="ghost-text"
              data-testid="paperwork-add-cancel"
              onClick={() => {
                setIsAdding(false);
                setDraftKey('');
                setDraftDate('');
              }}
            >
              {t('detail.paperwork.addCancel')}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost-text"
            data-testid="paperwork-add-open"
            onClick={() => setIsAdding(true)}
            className="self-start"
          >
            {t('detail.paperwork.addMilestone')}
          </Button>
        )
      )}

      <Text component="span" variant="meta" tone="tertiary" className="leading-relaxed">
        {t('detail.paperwork.note')}
      </Text>
      <Text component="span" variant="meta" tone="tertiary" className="leading-relaxed">
        {t('detail.paperwork.financingNote')}
      </Text>
    </SurfaceCard>
  );
}

/**
 * One milestone: what it is, whether it has landed, and the date it landed on.
 *
 * @param props.milestone - The milestone definition.
 * @param props.value - The recorded date, or an empty string.
 * @param props.readOnly - Whether the date can still be changed.
 * @param props.onSave - Persists a new date for this milestone's field.
 * @returns The rendered row.
 */
function MilestoneRow({
  milestone,
  value,
  readOnly,
  onSave,
}: {
  milestone: SalePaperworkMilestoneType;
  value: string;
  readOnly: boolean;
  onSave: (milestoneId: string, value: string) => Promise<void>;
}) {
  const { t } = useTranslation('features/sales');
  const label = milestoneLabel(milestone, t);
  const recorded = Boolean(value);

  return (
    <div
      className="flex flex-wrap items-center gap-(--space-3) border-b border-(--border-subtle) py-(--space-3) last:border-b-0"
      data-testid={`paperwork-row-${milestone.id}`}
    >
      <div className="flex min-w-40 flex-1 flex-col gap-(--space-0-5)">
        <Text component="span" variant="bodySm" fw={500} tone="primary">
          {label}
        </Text>
        {/* A pending milestone says so on the row rather than being absent
          * from it. Listing only what is done hid how much was left. */}
        {!recorded && (
          <Text component="span" variant="meta" tone="tertiary">
            {t('detail.paperwork.noDateRecorded')}
          </Text>
        )}
      </div>

      {recorded && (
        <Pill accent={pillAccent(milestone.required)}>
          {t('detail.paperwork.recorded')}
        </Pill>
      )}

      {readOnly ? (
        <Text
          component="span"
          variant="bodySm"
          tone="secondary"
          className="min-w-28 text-right tabular-nums"
          data-testid={`paperwork-${milestone.id}-value`}
        >
          {formatCalendarDate(value) || '—'}
        </Text>
      ) : (
        <DateField
          value={value}
          aria-label={label}
          data-testid={`paperwork-${milestone.id}-input`}
          className="w-40"
          onChange={(e) => void onSave(milestone.id, e.target.value)}
        />
      )}
    </div>
  );
}

/**
 * The accent a recorded milestone's status pill is drawn in.
 *
 * @param gatesCompleteness - Whether the backend counts this milestone.
 * @returns The accent token, or undefined for the neutral pill.
 */
function pillAccent(gatesCompleteness: boolean): string | undefined {
  return gatesCompleteness ? 'var(--accent-salvia)' : undefined;
}

export default SalePaperworkPanel;
